import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { ENV } from "@app/constants/env";
import { getToken } from "@app/helpers/authProvider";
import { JobServiceEntity } from "@feature/types/jobservice.type";

// Types for the API
export interface GetAllWOServicesRequestBody {
  UserID: number;
  Mstr_Show_ID: number;
  PageNumber: number;
  PageSize: number;
  TotalRecord: number;
  Row_Number: number;
  filtered: string;
  SortColumn: string;
  SortOrder: string;
  WOType: string;
}

export interface GetAllWOServicesParams {
  sendMail: boolean;
  body: GetAllWOServicesRequestBody;
}

export interface WOServiceAuditParams {
  UserId: number;
  id: number;
}

export interface JobServiceByIdParams {
  mstrWoServiceId: number;
  userId: number;
  assignedTo: number;
}

// Add ApiResponse type definition
type ApiResponse<T> = {
  data: T;
  success: boolean;
  message?: string;
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: ENV.BASE_API_URL +ENV.JOB_APIURL,
});

const authBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (incomingArgs, api, extraOptions) => {
  let args: string | FetchArgs = incomingArgs;

  if (typeof args === "string") {
    args = { url: args };
  }

  try {
    const token = await getToken();
    
    
    if (token) {
      let headers: Headers | undefined;
      if ((args as FetchArgs).headers instanceof Headers) {
        headers = (args as FetchArgs).headers as Headers;
      } else if ((args as FetchArgs).headers) {
        headers = new Headers((args as FetchArgs).headers as HeadersInit);
      } else {
        headers = new Headers();
      }

      if (headers) {
        headers.set("Authorization", `Bearer ${token}`);
        (args as FetchArgs).headers = headers;
      }
    }

    const result = await rawBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401 && token) {
      // refresh token flow could go here
    }

    return result;
  } catch (error) {
    console.error('Token retrieval error in jobServiceSliceApi:', error);
    // Continue without token - let the API handle the authentication error
    const result = await rawBaseQuery(args, api, extraOptions);
    return result;
  }
};

// Create the API
export const jobServiceSliceApi = createApi({
  reducerPath: "jobServiceSliceApi",
  baseQuery: authBaseQuery,
  tagTypes: ["WOService"],
  endpoints: (builder) => ({
     getAllWOServices: builder.query<
      ApiResponse<JobServiceEntity[]>,
      GetAllWOServicesParams
    >({
      query: ({  body }) => ({
        url: `WorkOrderService/GetAllWOServices`,
        method: "POST",
        body: body,
      }),
      providesTags: ["WOService"],
    }),
   
    getJobServiceById: builder.query<any, JobServiceByIdParams>({
      query: ({ mstrWoServiceId, userId, assignedTo }) => ({
        url: `WorkOrderService/GetJobServiceById?mstrWoServiceId=${mstrWoServiceId}&userId=${userId}&assignedTo=${assignedTo}`,
        method: "GET",
        headers: {
          accept: "*/*",
        },
      }), 
      providesTags: (result, error, arg) => [
        { type: "WOService", id: arg.mstrWoServiceId },
      ],
    }),
    readWoServiceAudit: builder.query<any, WOServiceAuditParams>({
      query: ({ UserId, id }) => ({
        url: `WorkOrderService/ReadWoServiceAudit?UserId=${UserId}&id=${id}`,
        method: "GET",
        headers: {
          accept: "*/*",
        },
      }),
      providesTags: (result, error, arg) => [{ type: "WOService", id: arg.id }],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetAllWOServicesQuery,
  useGetJobServiceByIdQuery,
  useReadWoServiceAuditQuery,
} = jobServiceSliceApi;

